import { Box, Text } from "@mantine/core";
import { StatusBadgeHelp, StatusStepPopover, useStatusHelp } from "@dehub/theme";
import { useTranslation } from "react-i18next";
import type { ReturnRequestStatus } from "../../apis/return";
import { DotPill } from "../delivery/atoms";
import classes from "./workflow-strip.module.css";

// Canonical happy-path milestones. The three logistics sub-states collapse into one «Тээвэр» step.
const MILESTONE_KEYS = [
  "REQUESTED", "PROPOSED", "ACCEPTED", "TRANSPORT",
  "WAREHOUSE", "RESOLUTION", "PAYMENT", "COMPLETED"
];

// Each status maps to the milestone index it sits at on the happy path.
const STATUS_TO_MILESTONE: Record<ReturnRequestStatus, number> = {
  REQUESTED                 : 0,
  PROPOSED                  : 1,
  ACCEPTED                  : 2,
  AWAITING_PICKUP_SCHEDULE  : 3,
  AWAITING_SHIPMENT_INFO    : 3,
  IN_TRANSIT                : 3,
  IN_TRANSIT_EXCEPTION      : 3,
  AWAITING_WAREHOUSE_RECEIPT: 4,
  AWAITING_RESOLUTION       : 5,
  REFUND_PENDING            : 6,
  COMPLETED                 : 7,
  PARTIALLY_COMPLETED       : 7,
  REJECTED                  : 1,
  DISPUTED                  : 1,
  CANCELLED                 : 0
};

// Branch/terminal statuses: the strip stops at the branch point and ends with a status pill.
const BRANCH: Partial<Record<ReturnRequestStatus, { upTo: number; labelKey: string; tone: "red" | "grey" }>> = {
  REJECTED            : { upTo: 1, labelKey: "workflow.branch.REJECTED", tone: "grey" },
  CANCELLED           : { upTo: 0, labelKey: "workflow.branch.CANCELLED", tone: "grey" },
  DISPUTED            : { upTo: 1, labelKey: "workflow.branch.DISPUTED", tone: "red" },
  // §D13 — exception branches off at the Тээвэр (transport) milestone.
  IN_TRANSIT_EXCEPTION: { upTo: 3, labelKey: "workflow.branch.IN_TRANSIT_EXCEPTION", tone: "red" }
};

type StepState = "done" | "current" | "future";

/** Milestone → the `order.return-status` row its «?» popover explains (@dehub/help R5). */
const MILESTONE_STATUS: Record<string, ReturnRequestStatus> = {
  REQUESTED : "REQUESTED",
  PROPOSED  : "PROPOSED",
  ACCEPTED  : "ACCEPTED",
  TRANSPORT : "IN_TRANSIT",
  WAREHOUSE : "AWAITING_WAREHOUSE_RECEIPT",
  RESOLUTION: "AWAITING_RESOLUTION",
  PAYMENT   : "REFUND_PENDING",
  COMPLETED : "COMPLETED"
};

/** Numbered 8-step strip (canvas Ord_ReturnDetail): current = dark filled circle + bold label. */
export function ReturnWorkflowStrip({ status }: { status: ReturnRequestStatus }) {
  const { t } = useTranslation("returns");
  const stepHelp = useStatusHelp("order.return-status");
  const branch = BRANCH[status];
  const current = branch ? branch.upTo : STATUS_TO_MILESTONE[status];
  const milestones = branch ? MILESTONE_KEYS.slice(0, branch.upTo + 1) : MILESTONE_KEYS;

  return (
    <Box component="ol" className={classes.strip} aria-label={t("workflow.aria")} m={0}>
      {milestones.map((key, i) => {
        const state: StepState = i < current ? "done" : i === current ? "current" : "future";
        return (
          <Box component="li" key={key} className={classes.step} aria-current={state === "current" ? "step" : undefined}>
            {/* The popover button wraps the step's content, not the flex item, so the strip keeps its spacing. */}
            <StatusStepPopover help={stepHelp(MILESTONE_STATUS[key])} title={t(`workflow.step.${key}`)}>
              <Box style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "var(--mantine-spacing-xs)" }}>
                <Box component="span" className={classes.num} data-state={state}>{i + 1}</Box>
                <Text className={classes.label} data-state={state}>{t(`workflow.step.${key}`)}</Text>
              </Box>
            </StatusStepPopover>
          </Box>
        );
      })}
      {branch ? (
        <Box component="li" className={classes.step}>
          <StatusBadgeHelp helpNs="order.return-status" code={status} name={t(branch.labelKey)}>
            <DotPill tone={branch.tone}>{t(branch.labelKey)}</DotPill>
          </StatusBadgeHelp>
        </Box>
      ) : null}
    </Box>
  );
}
